import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { CheckCircle2, Info, Bell } from "lucide-react";

import PageTitle from "components/generic/PageTitle";
import Breadcrumbs from "components/generic/Breadcrumbs";


const priorityOptions = [
  { value: "1", label: "High" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Low" },
];

const categoryOptions = [
  { value: "1", label: "Setup" },
  { value: "2", label: "Testing" },
  { value: "3", label: "Operation" },
  { value: "4", label: "Breakdown" },
  { value: "5", label: "Post-Production" },
];

const ViewTaskPage = () => {
  const location = useLocation();
  const { task, crewMember } = location.state || {};

  const [profileImage, setProfileImage] = useState("/images/misc/profile.png");
  const crewName = `${crewMember?.first_name} ${crewMember?.last_name}` || ""

  return (
    <div className="add-task-content p-6">
      <>
        <Breadcrumbs />
        <PageTitle title={"Task Details"} description={`View the task assigned to ${crewName}`} />

        <div className="flex gap-6 self-stretch ">
          <div className="w-4/6">
            <div className="border border-border bg-card rounded-2xl items-start p-6">
              <div className="flex flex-col items-start gap-4 w-full">

                {/* TASK TITLE */}
                <div className="flex flex-col items-start gap-1.5 w-full">
                  <p className="text-foreground text-sm font-bold leading-4">Task Title</p>
                  <p className="text-foreground text-base">{task.title}</p>
                </div>

                {/* DESCRIPTION */}
                <div className="flex flex-col gap-1.5 w-full">
                  <p className="text-foreground text-sm font-bold leading-4">Description</p>
                  <p className="text-foreground text-base whitespace-pre-line">{task.description}</p>
                </div>

                {/* PRIORITY + CATEGORY */}
                <div className="flex gap-4 w-full">

                  <div className="w-full flex flex-col gap-1.5">
                    <p className="text-foreground text-sm font-bold leading-4">Priority Level</p>
                    <p className="text-base text-foreground">{priorityOptions.find(o => o.value === String(task.priority_id))?.label}</p>
                  </div>

                  <div className="w-full flex flex-col gap-1.5">
                    <p className="text-foreground text-sm font-bold leading-4">Category</p>
                    <p className="text-base text-foreground">{categoryOptions.find(o => o.value === String(task.category_id))?.label}</p>
                  </div>

                </div>

                {/* DATE + TIME */}
                <div className="flex gap-4 w-full">
                  <div className="w-full flex flex-col gap-1.5">
                    <p className="text-foreground text-sm font-bold leading-4">Due Date</p>
                    <p className="text-base">{task.due_date}</p>
                  </div>

                  <div className="w-full flex flex-col gap-1.5">
                    <p className="text-foreground text-sm font-bold leading-4">Due Time</p>
                    <p className="text-base">{task.due_time}</p>
                  </div>
                </div>

                {/* ESTIMATED DURATION */}
                <div className="flex flex-col items-start gap-1.5 w-full">
                  <p className="text-foreground text-sm font-bold leading-4">Estimated Duration</p>
                  <p className="text-base">{task.estimated_duration} hrs</p>
                </div>

                {/* DEPENDENCIES */}
                <div className="flex flex-col gap-1.5 w-full">
                  <p className="text-foreground text-sm font-bold leading-4">Dependencies</p>
                  <div className="text-base whitespace-pre-line">
                    <ul className="list-disc pl-6">
                      {(() => {
                        let deps = [];
                        try {
                          deps = JSON.parse(task.dependencies || "[]");
                        } catch (e) {
                          // fallback if it's not valid JSON → split by new line
                          deps = task.dependencies?.split("\n") || [];
                        }

                        return deps.map((d, i) => (
                          <li key={i} className="text-sm text-grey-primary">
                            {d}
                          </li>
                        ));
                      })()}
                    </ul>
                  </div>
                </div>

                {/* ADDITIONAL NOTES */}
                <div className="flex flex-col gap-1.5 w-full">
                  <p className="text-foreground text-sm font-bold leading-4">Additional Notes</p>
                  <p className="text-base whitespace-pre-line">{task.additional_notes}</p>
                </div>
              </div>
            </div>
            <div className="border border-border bg-card rounded-2xl items-start p-6 mt-4">
              <div className="flex flex-col items-start gap-4 w-full">
                {/* Checklist */}
                <p className="text-foreground text-base font-bold leading-4">Checklist</p>
                <div className="text-base whitespace-pre-line">
                  <ol className="list-disc pl-6">
                    {(() => {
                      let deps = [];
                      try {
                        deps = JSON.parse(task.checklist || "[]");
                      } catch (e) {
                        // fallback if it's not valid JSON → split by new line
                        deps = task.checklist?.split("\n") || [];
                      }

                      return deps.map((d, i) => (
                        <li key={i} className="text-sm text-grey-primary">
                          {d}
                        </li>
                      ));
                    })()}
                  </ol>
                </div>
              </div>

            </div>

          </div>
          <div className="w-2/6 flex flex-col justify-start items-start gap-6">
            <div className="flex flex-col justify-start items-start gap-4 bg-card rounded-2xl p-6 w-full">
              <div className=" text-2xl leading-normal font-semibold">Assigned To</div>
              <div
                className="w-full bg-button-generic rounded-xl border border-button-generic drop-shadow-sm p-3 flex items-center gap-3 mb-3 last:mb-0"
              >
                <img
                  src={profileImage}
                  alt={crewName}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold">
                    {crewName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default ViewTaskPage;
